import React, { FC, useState, Suspense, useEffect } from 'react';
import { Plural, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Formik, FormikProps, useField, FieldArray } from 'formik';
import { array, object, string, mixed, lazy as yupLazy } from 'yup';
import {
  Text,
  Box,
  SideSheet,
  SideSheetHeader,
  SideSheetContentCol,
  SideSheetFooter,
  Select,
  TextFieldSize,
  FormControl,
  Radio,
  Icon,
  IconSize,
  Spinner,
  useToastsDispatch,
  ToastType,
  ToastAction,
  SpinnerSize,
  theme,
  SideSheetBody,
} from '@bechtel/gds';
import { SectionHeader, ErrorRetry, ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { WeldProcedure } from '../../atoms';
import { useCrewMember, useRodFillerMaterialQuery, useRodRoomListQuery } from '~/hooks';
import { StencilSelector, WeldTagItem, RodRequestComments } from '~/components/molecules';
import { NewRodRequestProps, RodRequestFormikState, FillerMaterial } from './types';
import {
  RodRequestFooterButtonStyled,
  AddNewButtonStyled,
  FillerMaterialStyled,
  RodRequestSideSheetSubHeaderStyled,
  ListSelectionStyled,
  ClosedStyled,
  WeldStyled,
} from './styled';
import { map, find, orderBy, uniqBy, get, flatMap, uniq } from 'lodash';
import {
  RodFillerMaterial,
  RodRoom,
  Weld,
  CrewMemberList,
  RodRequest,
  RodRequestFillerMaterial,
  FilterOperator,
  RodRequestFilterByTerm,
  CreateRodRequestInput,
  CreateRodRequestPayload,
  CrewMemberDiscipline,
  RodDiameter,
  CrewMemberSortByTerm,
} from '@bechtel/types';
import { CloneRodRequest } from '../clone-rod-request';
import { NetworkStatus, useMutation, useApolloClient } from '@apollo/client';
import { CREATE_ROD_REQUEST } from '@bechtel/client-gql';
import { CreateRodRequestError, CreateRodRequestErrorType } from './error';
import { useRodRequestsQuery } from '~/hooks/use-rod-requests';

const formatFillerMaterials = (fillerMaterials: RodRequestFillerMaterial[]) => {
  const result: FillerMaterial[] = [];
  fillerMaterials.forEach((fillerMaterial) => {
    fillerMaterial?.diameters.forEach((rodDiameter) => {
      result.push({
        fillerMaterial: {
          id: fillerMaterial?.fillerMaterial?.id,
          label: fillerMaterial?.fillerMaterial?.label,
        },
        diameter: {
          id: rodDiameter?.id,
          label: rodDiameter?.label,
        },
      });
    });
  });
  return result;
};

export const CreateRodRequestToast = (requestNumber: string) => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`Rod Request with request number ${requestNumber} successfully made.`}
      </Text>
    </Box>
  );
};

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString();
};

export const pickupTime = [
  {
    label: t`Next Working Day `,
    helperText: t`(7am)`,
    value: getTomorrowDate(),
  },
  { label: t`Today`, helperText: t`(Available around 2 hours after request)`, value: new Date().toISOString() },
];

const ErrorField = ({ error }) => {
  const { i18n } = useLingui();
  return (
    <Box py={{ sm: '400' }}>
      <Text color="bechtel.red">{i18n._(error)}</Text>
    </Box>
  );
};

const Item = ({ item }: { item: Record<string, string> }) => {
  return (
    <Text size="400" color="grey.1000">
      {item.label ? item.label : item.name}
    </Text>
  );
};

const SelectedItem = ({ item }: { item: Record<string, string> }) => {
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {item.label ? item.label : item.name}
    </Text>
  );
};

export const PickupLocation = ({ name }) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const { loading, data } = useRodRoomListQuery();
  const rodRoomList: RodRoom[] = get(data, 'rodRooms', []);

  return (
    <Box mx={{ sm: '450' }} my={{ sm: '500' }}>
      <Select
        loading={loading}
        data-testid="pickup-location"
        selectedItem={find(rodRoomList, { id: field.value })}
        itemComponent={Item}
        selectedComponent={SelectedItem}
        size={TextFieldSize.LARGE}
        items={rodRoomList}
        onSelectedItemChange={async (changes) => {
          setValue(changes.selectedItem.id);
        }}
      />
      {meta.touched && meta.error ? <ErrorField error={meta.error} /> : null}
    </Box>
  );
};

export const PickupTime = ({ name }) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const { i18n } = useLingui();

  return (
    <Box flexDirection={{ sm: 'column' }} mx={{ sm: '450' }} data-testid="pickup-time-container">
      {map(pickupTime, (item) => {
        const selected = item.value === field.value;
        const onClick = () => {
          setValue(item.value);
        };
        return (
          <ListSelectionStyled key={item.value} data-testid={`select-pickup-time-${item.value}`} onClick={onClick}>
            <Box mr={{ sm: '450' }}>
              <Radio checked={selected} readOnly />
            </Box>
            <Box alignItems={{ sm: 'center' }}>
              <Text size="400" color="grey.1000" weight={selected ? '600' : '400'}>
                {i18n._(item.label)}
              </Text>
              &nbsp;
              <Text size="400" color="grey.1000" style={{ opacity: '0.5' }}>
                {i18n._(item.helperText)}
              </Text>
            </Box>
          </ListSelectionStyled>
        );
      })}
      {meta.touched && meta.error ? <ErrorField error={meta.error} /> : null}
    </Box>
  );
};

export const FillerMaterialList = ({ addFillerMaterial, removeFillerMaterial, form }) => {
  const { values, setFieldValue } = form;
  const { loading, data } = useRodFillerMaterialQuery();
  const rodFillerMaterialList: RodFillerMaterial[] = get(data, 'rodFillerMaterials.items', []);

  return (
    <Box flexDirection={{ sm: 'column' }}>
      {map(values.fillerMaterials, (fillerMaterialItem, rowIndex) => {
        const rodFillerMaterial = find(rodFillerMaterialList, (item) => {
          return item.id === fillerMaterialItem.fillerMaterial.id;
        });
        const rodDiameterList: RodDiameter[] = rodFillerMaterial
          ? map(rodFillerMaterial.rodFillerMaterialDetails, 'diameter')
          : [];

        return (
          <FillerMaterialStyled key={fillerMaterialItem.fillerMaterial.id + fillerMaterialItem?.diameter?.id}>
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'flex-start' }} ml={{ sm: '400' }}>
              <Text color="grey.1000" family="graphik" weight="600" size="400">{`#${rowIndex + 1}`}</Text>
            </Box>
            <Box ml={{ sm: '400' }} mr={{ sm: '200' }} my={{ sm: '400' }}>
              <FormControl label="" style={{ flexGrow: 1 }}>
                <Select
                  loading={loading}
                  data-testid="rod-fillerMaterial"
                  name={`fillerMaterials[${rowIndex}].fillerMaterial`}
                  selectedItem={fillerMaterialItem?.fillerMaterial}
                  items={rodFillerMaterialList}
                  itemComponent={Item}
                  selectedComponent={SelectedItem}
                  placeholder={t`Filler Material`}
                  size={TextFieldSize.LARGE}
                  onSelectedItemChange={async (changes) => {
                    setFieldValue(`fillerMaterials[${rowIndex}].fillerMaterial`, changes.selectedItem);
                  }}
                />
              </FormControl>
            </Box>
            <Box my={{ sm: '400' }} mx={{ sm: '200' }}>
              <FormControl label="" style={{ flexGrow: 1 }}>
                <Select
                  data-testid="rod-diameter"
                  name={`fillerMaterials[${rowIndex}].diameter`}
                  selectedItem={fillerMaterialItem?.diameter}
                  items={rodDiameterList}
                  itemComponent={Item}
                  selectedComponent={SelectedItem}
                  placeholder={t`Diameter`}
                  size={TextFieldSize.LARGE}
                  onSelectedItemChange={async (changes) => {
                    setFieldValue(`fillerMaterials[${rowIndex}].diameter`, changes.selectedItem);
                  }}
                />
              </FormControl>
            </Box>
            {values.fillerMaterials.length > 1 && (
              <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} css={{ cursor: 'pointer' }}>
                <Icon
                  icon="times-circle"
                  fillColor="bechtel.blue.800"
                  size={IconSize.MEDIUM}
                  onClick={() => {
                    removeFillerMaterial(rowIndex);
                  }}
                />
              </Box>
            )}
          </FillerMaterialStyled>
        );
      })}
      <Box ml={{ sm: '800' }} my={{ sm: '450' }} data-testid="add-new-button">
        <AddNewButtonStyled
          onClick={() => {
            addFillerMaterial({
              fillerMaterial: { id: '', label: '', value: '' },
              diameter: { id: '', label: '', value: '' },
            });
          }}
        >
          <Icon icon="plus" color="bechtel.blue.800" size={IconSize.MEDIUM} />
          <Box ml={{ sm: '200' }}>
            <Text color="grey.1000" size="300">
              <Trans>Add New</Trans>
            </Text>
          </Box>
        </AddNewButtonStyled>
      </Box>
    </Box>
  );
};

export const validationSchema = object().shape({
  weldProcedure: object().required(t`Weld Procedure is mandatory`),
  fillerMaterials: array().of(
    object().shape({
      fillerMaterial: yupLazy((value) => {
        if (value) {
          return object().shape({
            id: string().required(t`Filler Material is mandatory`),
          });
        }
        return mixed().required(t`Filler Material is mandatory`);
      }),
      diameter: yupLazy((value) => {
        if (value) {
          return object().shape({
            id: string().required(t`Diameter is mandatory`),
          });
        }
        return mixed().required(t`Diameter is mandatory`);
      }),
    })
  ),
  stencils: array()
    .required()
    .min(1, t`Select at least 1 stencil`),
  welds: array()
    .required()
    .min(1, t`Select at least 1 weld`),
  pickupLocation: string().required(t`Pickup location is mandatory`),
  pickupTime: string().required(t`Pickup time is mandatory`),
});

export const NewRodRequest: FC<NewRodRequestProps> = ({ onClose, welds }) => {
  const client = useApolloClient();
  const [crewMemberQuery, setCrewMemberQuery] = useState('');
  const [
    getWelderList,
    {
      data: crewMemberData,
      error: crewMemberError,
      loading: crewMemberLoading,
      refetch: crewMemberRefetch,
      networkStatus: crewMemberNetworkStatus,
    },
  ] = useCrewMember();

  useEffect(() => {
    if (crewMemberQuery.length > 0 && crewMemberQuery.length < 2) return;
    getWelderList({
      variables: {
        input: {
          discipline: CrewMemberDiscipline.WELDING,
          sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
          searchKeyword: crewMemberQuery,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [getWelderList, crewMemberQuery]);

  const initialValues: RodRequestFormikState = {
    welds: welds,
    weldProcedure: '',
    stencils: [],
    fillerMaterials: [{ fillerMaterial: { id: '', label: '' }, diameter: { id: '', label: '' } }],
    pickupLocation: '',
    pickupTime: '',
    comments: '',
  };
  const [isOpen, setOpen] = useState(false);
  const [createRodRequestError, setCreateRodRequestError] = useState<CreateRodRequestErrorType | null>(null);
  const dispatchToast = useToastsDispatch();

  let form: FormikProps<RodRequestFormikState>;
  const cloneRequest = (rodRequest: RodRequest) => {
    if (form) {
      form.setValues({
        welds: welds,
        weldProcedure: rodRequest.weldProcedure,
        fillerMaterials: formatFillerMaterials(rodRequest.fillerMaterials),
        stencils: map(rodRequest.stencils, (item) => item.stencil),
        pickupLocation: rodRequest.rodRoom.id,
        pickupTime: '',
        comments: rodRequest?.comments?.length > 0 ? orderBy(rodRequest.comments, 'createdAt', 'desc')[0].comment : '',
      });
    }
  };

  const welderList = get(crewMemberData, 'crewMembers.items', []) as CrewMemberList['items'];
  const [createRodRequest, { loading: mutationLoading }] = useMutation<
    { createRodRequest: CreateRodRequestPayload },
    { input: CreateRodRequestInput }
  >(CREATE_ROD_REQUEST, {
    onCompleted: (data) => {
      const error = get(data, 'createRodRequest.error');
      if (error) {
        setCreateRodRequestError(error);
      } else {
        setCreateRodRequestError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => CreateRodRequestToast(get(data, 'createRodRequest.rodRequest.requestNumber', '')),
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        onClose();
        client.refetchQueries({
          include: ['crewProgressStats'],
        });
      }
    },
    onError: (error) => {
      setCreateRodRequestError(error);
    },
  });

  const [getRodRequests, { data, loading: rodRequestLoading }] = useRodRequestsQuery();
  const rodRequests = get(data, 'rodRequests.items', []);

  useEffect(() => {
    if (!welds || welds.length === 0 || !welds[0].tag) return;
    getRodRequests({
      variables: {
        input: {
          complexFilter: {
            and: [
              {
                term: RodRequestFilterByTerm.WELD_TAG,
                value: welds[0].tag,
                operator: FilterOperator.EQUALS,
              },
            ],
          },
        },
      },
    });
  }, [getRodRequests, welds]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values: RodRequestFormikState) => {
        const createRodRequestInput: CreateRodRequestInput = {
          weldIds: map(values.welds, (weld) => weld.id),
          weldProcedureId: values?.weldProcedure?.id,
          stencilIds: map(values.stencils, (stencil) => stencil.id),
          rodRoomId: values.pickupLocation,
          pickUpTime: values.pickupTime,
          comments: values.comments,
          fillerMaterialDiameterIds: map(flatMap(values.fillerMaterials, 'diameter'), 'id'),
        };
        createRodRequest({ variables: { input: createRodRequestInput } });
      }}
    >
      {(props: FormikProps<RodRequestFormikState>) => {
        const { handleSubmit, dirty, isValid, values } = props;
        form = props;
        return (
          <SideSheet onDismiss={close} visible>
            <SideSheetHeader title={t`New Rod Request`}>
              <ClosedStyled onClick={onClose}></ClosedStyled>
            </SideSheetHeader>
            {values.welds?.length > 1 && (
              <RodRequestSideSheetSubHeaderStyled>
                <Box alignItems={{ sm: 'center' }} ml={{ sm: '500' }}>
                  <Text family="graphik" color="grey.1000" weight="800" size="500">{`(${values.welds?.length})`}</Text>
                  &nbsp;
                  <Text color="grey.1000" family="graphik" size="500">
                    <Plural value={values.welds.length} one={`Weld Tag Selected`} other={`Weld Tags Selected`} />
                  </Text>
                </Box>
                <Box
                  onClick={() => {
                    setOpen(!isOpen);
                  }}
                  mr={{ sm: '600' }}
                  css={{ cursor: 'pointer' }}
                >
                  <Icon
                    size={IconSize.LARGE}
                    icon={isOpen ? 'chevron-down' : 'chevron-right'}
                    fillColor="bechtel.blue.800"
                  />
                </Box>
              </RodRequestSideSheetSubHeaderStyled>
            )}
            {(values.welds?.length == 1 || isOpen) && (
              <WeldStyled isOneWeldSelected={values.welds?.length == 1} data-testid="weld-tag-container">
                <FieldArray name="welds">
                  {({ remove }) => {
                    return map(values.welds, (weld: Weld, index) => {
                      return (
                        <Box
                          css={`
                            border-bottom: 1px solid ${({ theme }) => theme.colors.grey[200]};
                          `}
                          key={weld.id}
                          py={{ sm: '400' }}
                        >
                          <WeldTagItem
                            item={weld}
                            showDelete={values.welds?.length > 1}
                            onDelete={() => {
                              remove(index);
                            }}
                          />
                        </Box>
                      );
                    });
                  }}
                </FieldArray>
                {welds.length === 1 && rodRequestLoading && (
                  <Spinner
                    size={SpinnerSize.SMALL}
                    style={{ position: 'absolute', top: theme.spacings[500], right: theme.spacings[500] }}
                  />
                )}
              </WeldStyled>
            )}
            <SideSheetBody>
              <SideSheetContentCol data-testid="new-rod-request-container">
                {welds.length === 1 &&
                  map(uniqBy(orderBy(rodRequests, 'createdAt', 'desc'), 'weldProcedure.procedure'), (rodRequest) => {
                    return (
                      <CloneRodRequest key={rodRequest.id} rodRequest={rodRequest} cloneRodRequest={cloneRequest} />
                    );
                  })}
                <SectionHeader title={t`Weld Procedure(s)`} />
                <WeldProcedure
                  items={uniq(flatMap(values.welds, 'applicableWeldProcedures'))}
                  fieldName="weldProcedure"
                />
                <SectionHeader title={t`Filler Material`} />
                <FieldArray name="fillerMaterials">
                  {({ push, remove }) => {
                    return <FillerMaterialList addFillerMaterial={push} removeFillerMaterial={remove} form={props} />;
                  }}
                </FieldArray>
                <SectionHeader title={t`Stencil(s)`} />
                <Box mx={{ sm: '450' }} my={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                  {!crewMemberLoading && crewMemberError && (
                    <div data-testid="welder-error">
                      <ErrorRetry refetch={crewMemberRefetch}>
                        <Trans>There was a problem loading Stencils</Trans>
                      </ErrorRetry>
                    </div>
                  )}
                  {!crewMemberError && (
                    <StencilSelector
                      hideDropDownIcon={true}
                      fieldName="stencils"
                      items={welderList}
                      onSearch={(query) => setCrewMemberQuery(query)}
                      enableClientSearch={false}
                      loading={crewMemberLoading || crewMemberNetworkStatus === NetworkStatus.refetch}
                    />
                  )}
                </Box>
                <SectionHeader title={t`Select Pickup Location`} />
                <PickupLocation name="pickupLocation" />
                <SectionHeader title={t`Pick Up time`} />
                <PickupTime name="pickupTime" />
                <SectionHeader title={t`Comments`} />
                <RodRequestComments fieldName="comments" />
              </SideSheetContentCol>
            </SideSheetBody>
            <SideSheetFooter>
              <Box justifyContent={{ sm: 'flex-end' }}>
                <Box mr={{ sm: '400' }}>
                  <RodRequestFooterButtonStyled
                    data-testid="request-cancel"
                    onClick={onClose}
                    disabled={mutationLoading}
                  >
                    <Trans> Cancel</Trans>
                  </RodRequestFooterButtonStyled>
                </Box>
                <RodRequestFooterButtonStyled
                  data-testid="request-submit"
                  disabled={mutationLoading || !dirty || !isValid}
                  onClick={() => handleSubmit()}
                >
                  {mutationLoading ? <Spinner data-testid="mutation-loading" /> : <Trans>Review &amp; Submit</Trans>}
                </RodRequestFooterButtonStyled>
              </Box>
            </SideSheetFooter>
            {createRodRequestError && (
              <ErrorBoundary>
                <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                  <CreateRodRequestError error={createRodRequestError} close={() => setCreateRodRequestError(null)} />
                </Suspense>
              </ErrorBoundary>
            )}
          </SideSheet>
        );
      }}
    </Formik>
  );
};
